import CONSTANTS from '@/helpers/constants';
import { i18n } from '@/locales/i18n';
import { ListQueryParams, TimeFormat } from '@/types/enums';
import PersianDate from '@alireza-ab/persian-date';
import jwtDecode from 'jwt-decode';

export const isAftaMode = () => import.meta.env.VITE_IS_AFTA === 'true';

export const getExtension = (pathOrUrl: string): string => {
  return pathOrUrl.split(/[#?]/)[0].split('.').pop()?.trim().toLowerCase() ?? '';
};

export const getProperQueryString = (queryParams: ListQueryParams) => {
  const queryString = Object.keys(queryParams)
    .map((key) => {
      const value = queryParams[key as keyof ListQueryParams];

      if (value !== undefined) {
        if (Array.isArray(value)) {
          // If the value is an array, create multiple segments with the same key
          return value.map((item) => `${key}=${encodeURIComponent(item)}`).join('&');
        } else {
          // Otherwise, treat it as a single value
          return `${key}=${encodeURIComponent(value.toString())}`;
        }
      }
    })
    .filter((item) => !!item)
    .join('&');

  return queryString;
};

export const downloadURL = (url: string, callBack?: (status: 'LOADING' | 'COMPLETED') => any) => {
  const hiddenIFrameID = 'hiddenDownloader';
  let iframe = document.getElementById(hiddenIFrameID) as HTMLIFrameElement;

  if (iframe === null) {
    iframe = document.createElement('iframe');
    iframe.id = hiddenIFrameID;
    iframe.style.display = 'none';
    document.body.appendChild(iframe);
  }

  iframe.src = url;

  if (callBack) {
    callBack('LOADING');
  }

  iframe.onload = () => {
    if (callBack) {
      callBack('COMPLETED');
    }
  };
};

export const timestampToDate = (timeStamp: number | string | undefined) => {
  if (timeStamp) {
    const isLocaleFa = i18n.global.locale.value === 'fa';

    return new Date(timeStamp)
      .toLocaleDateString(isLocaleFa ? 'fa-IR' : 'en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
      .replace(/([۰-۹])/g, (token) => String.fromCharCode(token.charCodeAt(0) - 1728));
  }
  return null;
};

export const timestampToTime = (timeStamp: number | string | undefined, format = TimeFormat.HMS) => {
  if (timeStamp) {
    const fullTime = new Date(timeStamp).toTimeString().split(' ')[0];
    switch (format) {
      case TimeFormat.HMS: {
        return fullTime;
      }
      case TimeFormat.HM: {
        return fullTime.slice(0, -3);
      }
      default: {
        return fullTime;
      }
    }
  }

  return null;
};

export const EnglishNumberToPersian = (number: string | null | undefined) => {
  if (number) {
    // @ts-ignore
    return number.replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d]);
  }
  return null;
};

export const convertTimeStampToYmHMS = (
  timeStamp: number | string | undefined,
  separator = '-',
  dateTime = true,
  englishNumber = false,
): string | null => {
  const isLocaleFa = i18n.global.locale.value === 'fa';

  if (timeStamp) {
    const time = new Date(timeStamp).toTimeString().split(' ')[0];

    let date = new Date(timeStamp)
      .toLocaleDateString(isLocaleFa ? 'fa-IR' : 'en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
      .replace(/([۰-۹])/g, (token) => String.fromCharCode(token.charCodeAt(0) - 1728));
    if (separator === '-') date = date.replaceAll('/', '-');

    if (dateTime) return englishNumber ? `${date} - ${time}` : `${time} - ${date}`;
    return `${time}`;
  }
  return null;
};

export const convertTimestampToDate = (timeStamp: number | string | undefined): string | null => {
  const isLocaleFa = i18n.global.locale.value === 'fa';

  if (timeStamp) {
    const date = new Date(timeStamp)
      .toLocaleDateString(isLocaleFa ? 'fa-IR' : 'en-US', {
        year: 'numeric',
        month: 'long',
        day: '2-digit',
      })
      .replace(/([۰-۹])/g, (token) => String.fromCharCode(token.charCodeAt(0) - 1728))
      .replaceAll('/', '-');

    return date;
  }
  return null;
};

export const convertSecondsToHMS = (seconds: number | undefined): string => {
  if (seconds || seconds === 0) return new Date(seconds * 1000).toISOString().substr(11, 8);
  return '';
};

export const formatSecondsAsTime = (seconds: number | undefined, hideSeconds?: boolean): string => {
  if (seconds == null) return '';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  const time = [hours, minutes];

  if (!hideSeconds) {
    time.push(secs);
  }

  return time.map((val) => val.toString().padStart(2, '0')).join(':');
};

export const convertSecondsToHMPersian = (totalSeconds: number) => {
  const isNegative = totalSeconds < 0;
  if (isNegative) totalSeconds = totalSeconds * -1;

  const totalMinutes = Math.ceil(totalSeconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  let persianTime = '';

  if (hours)
    persianTime += i18n.global.t('records.remaining-time-hours-value', {
      hours: `${hours}${isNegative ? '-' : ''}`,
    });

  if (!hours && minutes)
    persianTime += i18n.global.t('records.remaining-time-minutes-value', {
      minutes: `${minutes}${isNegative ? '-' : ''}`,
    });

  if (hours && minutes)
    persianTime +=
      ' و ' +
      i18n.global.t('records.remaining-time-minutes-value', {
        minutes: `${minutes}${isNegative ? '-' : ''}`,
      });

  return persianTime;
};

export const differenceTimeStampFromDaysAgo = (timestamp: number | undefined): { days: number; hours: number } => {
  const date = new Date(timestamp!);
  const now = new Date();
  let difference = date.getTime() - now.getTime();

  const differenceTime = difference / 1000 / 60 / 60 / 24;
  const daysDifference = Math.floor(differenceTime);
  const hoursDifference = Math.floor((differenceTime - daysDifference) * 24);

  difference -= daysDifference * 1000 * 60 * 60 * 24;

  return { days: daysDifference, hours: hoursDifference };
};

export const formatBytes = (bytes: number | undefined | null) => {
  if (!bytes) return '0 bytes';

  if (bytes < 1024) {
    return bytes + ' bytes';
  } else if (bytes < 1024 * 1024) {
    const kb = (bytes / 1024).toFixed(2);
    return Number(kb) % 1 === 0 ? parseInt(kb) + ' KB' : kb + ' KB';
  } else if (bytes < 1024 * 1024 * 1024) {
    const mb = (bytes / (1024 * 1024)).toFixed(2);
    return Number(mb) % 1 === 0 ? parseInt(mb) + ' MB' : mb + ' MB';
  } else if (bytes < 1024 * 1024 * 1024 * 1024) {
    const gb = (bytes / (1024 * 1024 * 1024)).toFixed(2);
    return Number(gb) % 1 === 0 ? parseInt(gb) + ' GB' : gb + ' GB';
  } else {
    const tb = (bytes / (1024 * 1024 * 1024 * 1024)).toFixed(2);
    return Number(tb) % 1 === 0 ? parseInt(tb) + ' TB' : tb + ' TB';
  }
};

export const decodeHTML = (text: string) => {
  const txt = document.createElement('textarea');
  txt.innerHTML = text;

  return txt.value;
};

/**
 * Returns a JS object representation of a Javascript Web Token from its common encoded
 * string form.
 *
 * @template T the expected shape of the parsed token
 * @param {string} token a Javascript Web Token in base64 encoded, `.` separated form
 * @returns {(T | undefined)} an object-representation of the token
 * or undefined if parsing failed
 */
// export function getParsedJwt<T extends object = { [k: string]: string | number }>(token: string): T | undefined {
//   try {
//     return jwtDecode(token);
//   } catch (e) {
//     console.error(e);
//     return undefined;
//   }
// }

export function deepClone(objOrArr: Record<string, any> | []) {
  return JSON.parse(JSON.stringify(objOrArr));
}

export function replacePersianNumbersWithEnglish(text: string) {
  return text.replace(/[۰-۹]/g, (match) => String.fromCharCode(match.charCodeAt(0) - 1728));
}

export function formatNumberToMinutesSeconds(seconds: number) {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  const formattedMinutes = minutes < 10 ? `${minutes}` : `${minutes}`;
  const formattedSeconds = remainingSeconds < 10 ? `0${remainingSeconds}` : `${remainingSeconds}`;

  return `${formattedMinutes}:${formattedSeconds}`;
}

export function formatRialMoney(amount: number) {
  return new Intl.NumberFormat('fa-IR').format(amount);
}

export function injectGoftinoScript() {
  // @ts-ignore
  if (window.Goftino) return;

  const body = document.querySelector('body') as HTMLBodyElement;

  const scriptContent = `
      !(function () {
        var i = 'eudQyN',
          a = window,
          d = document;
        function g() {
          var g = d.createElement('script'),
            s = 'https://www.goftino.com/widget/' + i,
            l = localStorage.getItem('goftino_' + i);
          g.async = !0;
          g.src = l ? s + '?o=' + l : s;
          d.getElementsByTagName('head')[0].appendChild(g);
        }
        'complete' === d.readyState
          ? g()
          : a.attachEvent
          ? a.attachEvent('onload', g)
          : a.addEventListener('load', g, !1);
      })();
  `;

  const scriptElement = document.createElement('script');
  scriptElement.type = 'text/javascript';
  scriptElement.innerHTML = scriptContent;

  body.appendChild(scriptElement);
}

export const createUrlQueryWithTableQuery = (query: {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: string;
  search?: string;
}) => {
  const page = query.page && query.page > CONSTANTS.DEFAULT_PAGE ? query.page : null;

  return {
    ...(page ? { page } : {}),
    ...(query.pageSize && query.pageSize > CONSTANTS.DEFAULT_PAGE_SIZE ? { pageSize: query.pageSize } : {}),
    ...(query.sortBy && query.sortOrder ? { sortBy: query.sortBy } : {}),
    ...(query.sortOrder ? { sortOrder: query.sortOrder } : {}),
    ...(query.search ? { search: query.search } : {}),
  };
};

export const createTablePaginationQuery = (query: { page?: string; pageSize?: string }) => {
  let pageSize = Number(query.pageSize);
  const validPageSizes = CONSTANTS.PAGE_SIZES.map((ps) => ps.value);
  if (!validPageSizes.includes(pageSize)) {
    pageSize = CONSTANTS.DEFAULT_PAGE_SIZE;
  }

  return {
    page: query.page ? Number(query.page) : CONSTANTS.DEFAULT_PAGE,
    pageSize,
  };
};

export function isString(myVar: any) {
  return typeof myVar === 'string' || myVar instanceof String;
}

export function isObject(myVar: any) {
  return typeof myVar === 'object' && !Array.isArray(myVar) && myVar !== null;
}

export function cleanupImportedString(value: any): any {
  if (value == null || isObject(value)) return value;

  if (Array.isArray(value)) return (value as Array<string>).map(cleanupImportedString);

  return value
    .toString()
    .trim()
    .replace(/^\s+|\s+$/g, '')
    .replace(/\\n+/g, '')
    .replace(/\\r+/g, '');
}

export function uniquefyArray<T = string>(array: Array<T>): Array<T> {
  return [...new Set(array)];
}

export function deletePropertyFromObject<T extends Record<string, any>, K extends keyof T>(object: T, key: K) {
  Reflect.deleteProperty(object, key);
}

export function isAccessTokenExist(): boolean {
  const token = extractAccessTokenFromCookies();

  return typeof token === 'string' && token.length > 0;
}

export const extractAccessTokenFromCookies = (): string | null => {
  const token = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${CONSTANTS.COOKIE.ACCESS_TOKEN}=`))
    ?.split('=')[1];

  return typeof token === 'string' && token.length > 0 ? token : null;
};

export function extractUserIdFromAccessToken(token: string | undefined | null): string | null {
  if (!token) return null;

  const decoded = jwtDecode(token);

  if (decoded && typeof decoded === 'object' && 'customerId' in decoded) {
    return String((decoded as { [key: string]: any })['customerId']);
  }

  return null;
}

export function extractPayloadFromAccessToken(token: string | undefined | null): unknown {
  if (!token) return null;

  const decoded = jwtDecode(token);

  if (decoded && typeof decoded === 'object') {
    return decoded;
  }

  return null;
}

export const guessUsernameType = (username: string | null | undefined): 'mobile' | 'email' | 'username' | null => {
  if (!username) return null;

  if (CONSTANTS.REGEX.MOBILE.test(username)) {
    return 'mobile';
  }

  if (CONSTANTS.REGEX.EMAIL.test(username)) {
    return 'email';
  }

  return 'username';
};

export const stringToRegexp = (str: string | undefined): RegExp => {
  const cleanedStr = str?.replace(/^\/|\/$/g, '');

  return new RegExp(cleanedStr || /.*/);
};

export const prependZero = (num: number | string) => {
  if (typeof num === 'number') {
    return num.toString().padStart(2, '0');
  }

  return num.padStart(2, '0');
};

export const getEndOfToday = () => {
  // one minute to end of this day
  return new PersianDate().startOf('d').addMinute(1439);
};

export const getStartOfToday = () => {
  return new PersianDate().startOf('d');
};

export const guessTimestamp = (value: unknown): { isTimestamp: boolean; date?: Date } => {
  const num = typeof value === 'string' ? Number(value.trim()) : Number(value);

  if (!Number.isFinite(num) || !Number.isInteger(num) || num <= 0) {
    return { isTimestamp: false };
  }

  const MIN_SEC = 1577836800; // 2020-01-01
  const MAX_SEC = 2208988800; // 2040-01-01

  if (num >= MIN_SEC && num <= MAX_SEC) {
    return { isTimestamp: true, date: new Date(num * 1000) };
  }

  if (num >= MIN_SEC * 1000 && num <= MAX_SEC * 1000) {
    return { isTimestamp: true, date: new Date(num) };
  }

  return { isTimestamp: false };
};

export function deleteCookie(name: keyof typeof CONSTANTS.COOKIE) {
  document.cookie = CONSTANTS.COOKIE[name] + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
}

export function getCookie(name: keyof typeof CONSTANTS.COOKIE) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${CONSTANTS.COOKIE[name]}=`);

  if (parts.length === 2) {
    return parts.pop()?.split(';').shift();
  }

  return undefined;
}

export const decodeBase64Token = (base64: string) => {
  const binaryString = atob(base64);
  const bytes = Uint8Array.from(binaryString, (char) => char.charCodeAt(0));
  return new TextDecoder('utf-8').decode(bytes);
};

/**
 * Returns the difference between two unix ms timestamps,
 * in months if >= 1 month, otherwise in days.
 */
export const getTimestampDiff = (timestamp1: number, timestamp2: number): { value: number; unit: 'Day' | 'Month' } => {
  const diffMs = Math.abs(timestamp1 - timestamp2);
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 30) {
    return { value: diffDays, unit: 'Day' };
  }

  const diffMonths = Math.floor(diffDays / 30);
  return { value: diffMonths, unit: 'Month' };
};
